import { useCallback } from 'react'
import axios from 'axios'
import { getDatabase, ref, get, set, push, runTransaction, update, remove } from 'firebase/database'
import {
  getStorage,
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
} from 'firebase/storage'
import { uid } from 'uid'
import { auth } from '../auth/firebase'
import { ODUL_ONERI_RTDB_ROOT } from '../helper/odulOneriDepartments'
import { createEvaluationToken } from '../helper/odulOneriToken'
import { toastErrorNotify, toastSuccessNotify, toastWarnNotify } from '../helper/ToastNotify'
import { MANAGER_DECISION } from '../helper/odulOneriManagerReasons'
import { OPEX_DECISION } from '../helper/odulOneriOpexReasons'
import { resolveOdulOneriMailTarget } from '../helper/odulOneriMail'
import { resolveRelatedDepartmentManager } from '../helper/odulOneriDepartmentResolve'
import {
  REMINDER_AFTER_DAYS,
  addDaysIso,
  canSendReminder,
  isPastReminderThreshold,
} from '../helper/odulOneriReminders'
import {
  formatUygulamaNumber,
  buildImplementationFromAccept,
  validateAttachment,
  ATTACHMENT_MAX_FILES,
} from '../helper/odulOneriImplementation'
import {
  resolveDepartmentFacility,
  normalizeMemberFacilities,
} from '../helper/odulOneriFacilities'
import {
  RECORD_STATUS,
  BOARD_DECISION,
  isLeaderAccessClosed,
} from '../helper/odulOneriStatus'
import {
  getMeetingWeightedScore,
  criterionAverageFromVotes,
  validateVoteRow,
  getTopicWeights,
  getActiveCriteriaForTopic,
  formatWeightedScore,
} from '../helper/odulOneriScoring'
import { BOARD_CRITERIA } from '../helper/odulOneriCriteria'
import { repairOdulOneriText } from '../helper/odulOneriText'
import {
  TRACKING_ACCESS_ROLE,
  OPEX_ACCESS_ROLE,
  createTrackingAccessTokenPayload,
  createOpexAccessTokenPayload,
  emailMatchesImplementation,
  writeTrackingSession,
  writeOpexSession,
} from '../helper/odulOneriTrackingSession'

const ROOT = ODUL_ONERI_RTDB_ROOT
const DEFAULT_SLA_DAYS = 2

/** OPEX doğrulama / iptal / kapanışta lider tokenını geçersiz kıl */
const invalidateLeaderTokensForImplementation = async (
  db,
  implementationId,
  preferredTokenId = null
) => {
  if (!implementationId) return
  const now = new Date().toISOString()
  const mark = async (tokenId) => {
    if (!tokenId) return
    await update(ref(db, `${ROOT}/tokens/${tokenId}`), {
      isUsed: true,
      usedAt: now,
      closedReason: 'process_closed',
    })
  }
  if (preferredTokenId) {
    await mark(preferredTokenId)
    return
  }
  const snap = await get(ref(db, `${ROOT}/tokens`))
  const all = snap.val() || {}
  const ids = Object.entries(all)
    .filter(
      ([, t]) =>
        t?.role === 'implementation_leader' &&
        t?.implementationId === implementationId &&
        !t?.isUsed &&
        !t?.usedAt
    )
    .map(([id]) => id)
  await Promise.all(ids.map((id) => mark(id)))
}

const escapeHtml = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')

const formatRecordNumber = (year, sequence) =>
  `OS-${year}-${String(sequence).padStart(6, '0')}`

const sendOdulOneriMail = async ({ to, subject, message, meta = {} }, db = null) => {
  if (!to) return false
  const resolved = resolveOdulOneriMailTarget(to, message)
  try {
    await axios.post(`${process.env.REACT_APP_MAIL_ENDPOINT}/api/sendmail`, {
      to: resolved.to,
      cc: '',
      subject,
      message: resolved.message,
      digitalplatform: 'bonna-feedback',
    })
    return true
  } catch (error) {
    if (db) {
      try {
        await push(ref(db, `${ROOT}/mailFailures`), {
          to: resolved.originalTo || to,
          subject,
          message: resolved.message,
          error: error?.message || 'Mail gönderilemedi',
          recordId: meta.recordId || null,
          recordNumber: meta.recordNumber || null,
          createdAt: new Date().toISOString(),
          resolved: false,
        })
      } catch (logError) {
        console.log('mailFailures log:', logError)
      }
    }
    console.log('sendOdulOneriMail:', error)
    return false
  }
}

const claimUnusedToken = async (db, tokenId, { expectedRole = null, usedAt = null } = {}) => {
  const tokenRef = ref(db, `${ROOT}/tokens/${tokenId}`)
  const stamp = usedAt || new Date().toISOString()
  const preSnap = await get(tokenRef)
  const pre = preSnap.val()
  if (!pre) throw new Error('Geçersiz bağlantı: bağlantı bulunamadı.')
  if (expectedRole && pre.role !== expectedRole) {
    throw new Error('Bu bağlantı bu işlem için geçerli değil.')
  }
  if (pre.isUsed || pre.usedAt) {
    throw new Error('Bu değerlendirme bağlantısı daha önce kullanılmış.')
  }
  const tx = await runTransaction(
    tokenRef,
    (current) => {
      const base = current == null ? pre : current
      if (!base) return
      if (expectedRole && base.role !== expectedRole) return
      if (base.isUsed || base.usedAt) return
      return { ...base, isUsed: true, usedAt: stamp }
    },
    { applyLocally: false }
  )
  if (tx.committed) return tx.snapshot.val()
  const afterSnap = await get(tokenRef)
  const after = afterSnap.val()
  if (!after) throw new Error('Geçersiz bağlantı: bağlantı bulunamadı.')
  if (after.isUsed || after.usedAt) {
    throw new Error('Bu değerlendirme bağlantısı daha önce kullanılmış.')
  }
  await update(tokenRef, { isUsed: true, usedAt: stamp })
  return { ...after, isUsed: true, usedAt: stamp }
}

const hasValidEmail = (email) => String(email || '').trim().includes('@')

/** Aktif OPEX kullanıcıları — yalnızca config/opexUsers listesi (hardcoded yok). */
const mapActiveOpexUsers = (snapshotVal) => {
  if (!snapshotVal) return []
  return Object.entries(snapshotVal)
    .map(([id, user]) => ({
      id,
      fullName: String(user?.fullName || '').trim() || id,
      title: String(user?.title || '').trim(),
      email: String(user?.email || '').trim().toLowerCase(),
      active: user?.active !== false,
    }))
    .filter((user) => user.active && hasValidEmail(user.email))
    .sort((a, b) => a.fullName.localeCompare(b.fullName, 'tr'))
}

const loadActiveOpexUsers = async (db) => {
  const snap = await get(ref(db, `${ROOT}/config/opexUsers`))
  return mapActiveOpexUsers(snap.val())
}

const buildOpexAssignee = (opexUsers) => {
  const primary = opexUsers[0]
  const names = opexUsers.map((u) => u.fullName).filter(Boolean)
  return {
    type: 'opex',
    id: 'opex',
    name: names.length ? names.join(', ') : 'OPEX',
    email: primary?.email || '',
    emails: opexUsers.map((u) => u.email),
  }
}

const notifyActiveOpexUsers = async (db, opexUsers, { subject, messageHtml, meta = {} }) => {
  const list = Array.isArray(opexUsers) ? opexUsers : []
  if (!list.length) return { sent: 0, failed: 0 }
  const results = await Promise.all(
    list.map((user) =>
      sendOdulOneriMail(
        {
          to: user.email,
          subject,
          message: messageHtml,
          meta,
        },
        db
      )
    )
  )
  const sent = results.filter(Boolean).length
  return { sent, failed: results.length - sent }
}

const mapDepartmentsSnapshot = (snapshotVal) => {
  if (!snapshotVal) return []
  return Object.entries(snapshotVal)
    .map(([id, dept]) => ({
      id,
      name: dept?.name || id,
      facility: resolveDepartmentFacility(dept?.facility),
      managerName: dept?.managerName || '',
      managerEmail: dept?.managerEmail || '',
      active: dept?.active !== false,
    }))
    .filter((dept) => dept.active)
    .sort((a, b) => {
      const facilityCmp = a.facility.localeCompare(b.facility, 'tr')
      if (facilityCmp !== 0) return facilityCmp
      return a.name.localeCompare(b.name, 'tr')
    })
}

const mapBoardMembersSnapshot = (snapshotVal) => {
  if (!snapshotVal) return []
  return Object.entries(snapshotVal)
    .map(([id, member]) => ({
      id,
      fullName: member?.fullName || id,
      title: member?.title || '',
      email: member?.email || '',
      role: member?.role === 'baskan' ? 'baskan' : 'uye',
      facilities: normalizeMemberFacilities(member?.facilities),
      active: member?.active !== false,
    }))
    .filter((member) => member.active)
}

const nextSequence = async (db, path) => {
  const sequenceRef = ref(db, path)
  const sequenceTx = await runTransaction(sequenceRef, (current) =>
    (typeof current === 'number' ? current : 0) + 1
  )
  if (!sequenceTx.committed) throw new Error('Sıra numarası üretilemedi.')
  return sequenceTx.snapshot.val()
}

const pushHistory = async (db, recordId, type, actor, payload) => {
  await set(ref(db, `${ROOT}/history/${recordId}/${uid()}`), {
    type,
    actor,
    payload,
    createdAt: new Date().toISOString(),
  })
}

const useOdulOneriCall = () => {
  const getDb = () => getDatabase(auth.app)

  const fetchDepartments = useCallback(async () => {
    try {
      const snapshot = await get(ref(getDb(), `${ROOT}/config/departments`))
      return mapDepartmentsSnapshot(snapshot.val())
    } catch (error) {
      console.log('fetchDepartments:', error)
      return []
    }
  }, [])

  const fetchBoardMembers = useCallback(async () => {
    try {
      const snapshot = await get(ref(getDb(), `${ROOT}/config/boardMembers`))
      return mapBoardMembersSnapshot(snapshot.val())
    } catch (error) {
      console.log('fetchBoardMembers:', error)
      return []
    }
  }, [])

  const getSlaDays = async (db) => {
    try {
      const settingsSnap = await get(ref(db, `${ROOT}/config/settings`))
      return settingsSnap.val()?.slaDays || DEFAULT_SLA_DAYS
    } catch (_) {
      return DEFAULT_SLA_DAYS
    }
  }

  /** Başvuru → OPEX havuzu */
  const createOdulOneri = useCallback(
    async (formData) => {
      const db = getDb()
      const now = new Date()
      const year = now.getFullYear()
      try {
        const [departments, sequence, slaDays, opexUsers] = await Promise.all([
          fetchDepartments(),
          nextSequence(db, `${ROOT}/config/settings/sequence`),
          getSlaDays(db),
          loadActiveOpexUsers(db),
        ])
        const relatedManager = resolveRelatedDepartmentManager(
          {
            relatedDepartmentId: formData.relatedDepartmentId,
            relatedDepartmentName: formData.relatedDepartmentName,
            facility: formData.location,
          },
          departments
        )

        const recordNumber = formatRecordNumber(year, sequence)
        const recordId = uid()
        const dueAt = new Date(now.getTime() + slaDays * 24 * 60 * 60 * 1000).toISOString()

        const attachments = Array.isArray(formData.currentStateAttachments)
          ? formData.currentStateAttachments
          : []

        const record = {
          recordNumber,
          status: RECORD_STATUS.OPEX_PENDING,
          applicant: {
            fullName: repairOdulOneriText(formData.fullName),
            employeeDept: repairOdulOneriText(formData.employeeDept),
            role: repairOdulOneriText(formData.role),
            phone: formData.phone,
            email: formData.email || '',
            location: formData.location,
          },
          relatedDepartmentId: relatedManager.id,
          relatedDepartmentName: relatedManager.name,
          relatedDepartmentManagerName: relatedManager.managerName,
          relatedDepartmentManagerEmail: relatedManager.managerEmail,
          topic: repairOdulOneriText(formData.topic),
          currentState: repairOdulOneriText(formData.currentState),
          proposedState: repairOdulOneriText(formData.proposedState || ''),
          currentStateAttachments: attachments,
          createdAt: now.toISOString(),
          dueAt,
          closedAt: null,
          assignee: buildOpexAssignee(opexUsers),
          opexDecision: null,
          managerDecision: null,
          implementationId: null,
          boardDecision: null,
        }

        await update(ref(db), {
          [`${ROOT}/records/${recordId}`]: record,
          [`${ROOT}/history/${recordId}/${uid()}`]: {
            type: 'record_created',
            actor: formData.email || formData.fullName || 'applicant',
            payload: {
              message: 'Kayıt oluşturuldu; OPEX ön inceleme havuzuna düştü',
              recordNumber,
              relatedDepartmentName: relatedManager.name,
            },
            createdAt: now.toISOString(),
          },
        })

        // Kayıt bitti — mailleri arka planda gönder; form beklemesin.
        void (async () => {
          try {
            const jobs = []
        if (formData.email) {
              jobs.push(
                sendOdulOneriMail(
            {
              to: formData.email,
              subject: 'Ödül Öneri Başvuru Teyidi',
              message: `<div>
            <p>Başvurunuz başarıyla alınmıştır.</p>
            <p><b>Kayıt Numaranız:</b> ${escapeHtml(recordNumber)}</p>
                      <p>Öneriniz önce OPEX tarafından incelenecektir.</p>
          </div>`,
              meta: { recordId, recordNumber },
            },
            db
                ).then((sent) => {
                  if (!sent) toastWarnNotify('Başvuru teyit maili gönderilemedi; kuyruğa alındı.')
                })
              )
            }
            jobs.push(
              (async () => {
                let failed = 0
                for (const user of opexUsers) {
                  try {
                    const accessToken = createOpexAccessTokenPayload(user.email)
                    await set(ref(db, `${ROOT}/tokens/${accessToken.tokenId}`), {
                      ...accessToken,
                      recordId,
                    })
                    const opexFiltreUrl = `${window.location.origin}/odul-oneri/opex-filtre?access=${encodeURIComponent(accessToken.tokenId)}&recordId=${encodeURIComponent(recordId)}`
                    const sent = await sendOdulOneriMail(
                      {
                        to: user.email,
                        subject: `Yeni Ödül Öneri — OPEX Ön İnceleme (${recordNumber})`,
                        message: `<div>
                  <p>Sayın ${escapeHtml(user.fullName || '')},</p>
                  <p>Yeni bir ödül öneri başvurusu OPEX ön inceleme havuzuna düştü.</p>
                  <p><b>Kayıt No:</b> ${escapeHtml(recordNumber)}</p>
                  <p><b>Başvuran:</b> ${escapeHtml(formData.fullName || '')}</p>
                  <p><b>Konu:</b> ${escapeHtml(formData.topic || '')}</p>
                  <p><b>İlgili bölüm:</b> ${escapeHtml(relatedManager.name || '')}</p>
                  <p>Bağlantı e-posta adresinizi doğrular; hesap açmanız gerekmez.</p>
                  <p><a href="${escapeHtml(opexFiltreUrl)}">Başvuruyu OPEX ön incelemede aç</a></p>
                  <p style="color:#667085;font-size:12px">Bağlantı 48 saat geçerlidir.</p>
                </div>`,
                        meta: { recordId, recordNumber },
                      },
                      db
                    )
                    if (!sent) failed += 1
                  } catch (e) {
                    console.log('opex notify user:', e)
                    failed += 1
                  }
                }
                if (opexUsers.length && failed > 0) {
                  toastWarnNotify('Bazı OPEX bildirim mailleri gönderilemedi; kuyruğa alındı.')
                }
              })()
            )
            await Promise.all(jobs)
          } catch (mailError) {
            console.log('createOdulOneri background mail:', mailError)
          }
        })()

        toastSuccessNotify(`Başvurunuz alındı. Kayıt No: ${recordNumber}`)
        return { recordId, recordNumber }
      } catch (error) {
        console.log('createOdulOneri:', error)
        toastErrorNotify(error?.message || 'Başvuru kaydı oluşturulamadı.')
        throw error
      }
    },
    [fetchDepartments]
  )

  const uploadCurrentStateAttachments = useCallback(async (files = []) => {
    const list = Array.from(files || []).slice(0, ATTACHMENT_MAX_FILES)
    if (!list.length) return []
    return Promise.all(
      list.map(async (file) => {
        const err = validateAttachment(file)
        if (err) throw new Error(err)
        const attachmentId = uid()
        const path = `${ROOT}/basvuru/${attachmentId}_${file.name}`
        const sRef = storageRef(getStorage(auth.app), path)
        await uploadBytes(sRef, file)
        const url = await getDownloadURL(sRef)
        return {
          id: attachmentId,
          name: file.name,
          size: file.size,
          contentType: file.type || '',
          storagePath: path,
          url,
          uploadedAt: new Date().toISOString(),
        }
      })
    )
  }, [])

  /** OPEX doğrulama sonuç ekleri (kurul «Sonuç ekleri») */
  const uploadResultAttachments = useCallback(
    async (implementationId, files = []) => {
      const list = Array.from(files || []).slice(0, ATTACHMENT_MAX_FILES)
      if (!list.length) return []
      const implKey = String(implementationId || 'unknown').trim() || 'unknown'
      return Promise.all(
        list.map(async (file) => {
          const err = validateAttachment(file)
          if (err) throw new Error(err)
          const attachmentId = uid()
          const path = `${ROOT}/dogrulama/${implKey}/${attachmentId}_${file.name}`
          const sRef = storageRef(getStorage(auth.app), path)
          await uploadBytes(sRef, file)
          const url = await getDownloadURL(sRef)
          return {
            id: attachmentId,
            name: file.name,
            size: file.size,
            contentType: file.type || '',
            storagePath: path,
            url,
            uploadedAt: new Date().toISOString(),
          }
        })
      )
    },
    []
  )

  const fetchOpexPendingRecords = useCallback(async () => {
    const snap = await get(ref(getDb(), `${ROOT}/records`))
    const all = snap.val() || {}
    return Object.entries(all)
      .filter(([, r]) => r?.status === RECORD_STATUS.OPEX_PENDING)
      .map(([id, r]) => ({ id, ...r }))
      .sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0))
  }, [])

  /** Mail deep-link: tek kayıt (OPEX bekleyen) */
  const fetchOdulOneriRecordById = useCallback(async (recordId) => {
    const id = String(recordId || '').trim()
    if (!id) return null
    const snap = await get(ref(getDb(), `${ROOT}/records/${id}`))
    const row = snap.val()
    if (!row) return null
    return { id, ...row }
  }, [])

  const fetchAllOdulOneriRecords = useCallback(async () => {
    const snap = await get(ref(getDb(), `${ROOT}/records`))
    const all = snap.val() || {}
    return Object.entries(all)
      .map(([id, r]) => ({ id, ...r }))
      .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
  }, [])

  /** OPEX ön filtre kararı */
  const submitOpexDecision = useCallback(
    async (recordId, payload, actorEmail = '') => {
      const db = getDb()
      const now = new Date().toISOString()
      try {
        const recordSnap = await get(ref(db, `${ROOT}/records/${recordId}`))
        const record = recordSnap.val()
        if (!record) throw new Error('Kayıt bulunamadı.')
        if (record.status !== RECORD_STATUS.OPEX_PENDING) {
          throw new Error('Bu kayıt OPEX kararında değil.')
        }

        if (payload.result === OPEX_DECISION.REJECT) {
          const reasons = payload.reasons || []
          if (!reasons.length) throw new Error('En az bir kapatma nedeni seçiniz.')
          if (!String(payload.justification || '').trim()) {
            throw new Error('Gerekçe zorunludur.')
          }
          await update(ref(db, `${ROOT}/records/${recordId}`), {
            status: RECORD_STATUS.CLOSED_NOT_SUGGESTION,
            closedAt: now,
            opexDecision: {
              result: OPEX_DECISION.REJECT,
              reasons,
              justification: String(payload.justification).trim(),
              evaluatorName: payload.evaluatorName || '',
              evaluatorEmail: actorEmail || '',
              decidedAt: now,
            },
            assignee: null,
            dueAt: null,
          })
          await pushHistory(db, recordId, 'opex_rejected', actorEmail || 'opex', {
            message: 'OPEX öneri olarak ilerletmedi',
            reasons,
          })
          if (record.applicant?.email) {
            await sendOdulOneriMail(
              {
                to: record.applicant.email,
                subject: `Öneri Sonucu — ${record.recordNumber}`,
                message: `<div>
                  <p>Sayın ${escapeHtml(record.applicant.fullName || '')},</p>
                  <p><b>${escapeHtml(record.recordNumber)}</b> numaralı öneriniz OPEX incelemesi sonucunda öneri olarak ilerletilmeyecektir.</p>
                  <p><b>Gerekçe:</b> ${escapeHtml(payload.justification)}</p>
                </div>`,
                meta: { recordId, recordNumber: record.recordNumber },
              },
              db
            )
          }
          toastSuccessNotify('Kayıt kapatıldı.')
          return { status: RECORD_STATUS.CLOSED_NOT_SUGGESTION }
        }

        // Forward to department manager
        const departments = await fetchDepartments()
        const targetDeptId = payload.targetDepartmentId || record.relatedDepartmentId
        const relatedManager = resolveRelatedDepartmentManager(
          {
            relatedDepartmentId: targetDeptId,
            relatedDepartmentName: payload.targetDepartmentName || record.relatedDepartmentName,
            facility: record.applicant?.location,
          },
          departments
        )
        const slaDays = await getSlaDays(db)
        const dueAt = addDaysIso(now, slaDays)
        const token = createEvaluationToken({
          recordNumber: record.recordNumber,
          assigneeId: relatedManager.id,
          role: 'manager',
        })

        await update(ref(db), {
          [`${ROOT}/records/${recordId}/status`]: RECORD_STATUS.MANAGER_PENDING,
          [`${ROOT}/records/${recordId}/relatedDepartmentId`]: relatedManager.id,
          [`${ROOT}/records/${recordId}/relatedDepartmentName`]: relatedManager.name,
          [`${ROOT}/records/${recordId}/relatedDepartmentManagerName`]: relatedManager.managerName,
          [`${ROOT}/records/${recordId}/relatedDepartmentManagerEmail`]: relatedManager.managerEmail,
          [`${ROOT}/records/${recordId}/dueAt`]: dueAt,
          [`${ROOT}/records/${recordId}/assignee`]: {
            type: 'manager',
            id: relatedManager.id,
            name: relatedManager.managerName,
            email: relatedManager.managerEmail,
          },
          [`${ROOT}/records/${recordId}/opexDecision`]: {
            result: OPEX_DECISION.FORWARD,
            note: payload.note || '',
            targetDepartmentId: relatedManager.id,
            targetDepartmentName: relatedManager.name,
            evaluatorName: payload.evaluatorName || '',
            evaluatorEmail: actorEmail || '',
            decidedAt: now,
          },
          [`${ROOT}/tokens/${token.tokenId}`]: {
            ...token,
            recordId,
            isUsed: false,
            usedAt: null,
          },
        })
        await pushHistory(db, recordId, 'opex_forwarded', actorEmail || 'opex', {
          message: `Bölüme yönlendirildi: ${relatedManager.name}`,
          managerEmail: relatedManager.managerEmail,
        })

        const evaluationUrl = `${window.location.origin}/odul-oneri/yonetici/${token.tokenId}`
        const sent = await sendOdulOneriMail(
          {
            to: relatedManager.managerEmail,
            subject: `Öneri Değerlendirme — ${record.recordNumber}`,
              message: `<div>
              <p>Sayın ${escapeHtml(relatedManager.managerName)},</p>
              <p>OPEX tarafından bölümünüze yönlendirilen bir öneri için kararınız beklenmektedir.</p>
            <p><b>Kayıt No:</b> ${escapeHtml(record.recordNumber)}</p>
              <p><b>Öneri Sahibi:</b> ${escapeHtml(record.applicant?.fullName || '')}</p>
              <p><b>Konu:</b> ${escapeHtml(record.topic || '')}</p>
              ${payload.note ? `<p><b>OPEX notu:</b> ${escapeHtml(payload.note)}</p>` : ''}
              <p><a href="${escapeHtml(evaluationUrl)}">Değerlendirme ekranını aç</a></p>
          </div>`,
            meta: { recordId, recordNumber: record.recordNumber },
            },
            db
          )
        if (!sent) toastWarnNotify('Yönetici maili gönderilemedi; kuyruğa alındı.')
        toastSuccessNotify('Öneri bölüm yöneticisine iletildi.')
        return { status: RECORD_STATUS.MANAGER_PENDING, tokenId: token.tokenId }
      } catch (error) {
        console.log('submitOpexDecision:', error)
        toastErrorNotify(error?.message || 'OPEX kararı kaydedilemedi.')
        throw error
      }
    },
    [fetchDepartments]
  )

  const getManagerEvaluationByToken = useCallback(async (tokenId) => {
    if (!tokenId) return { ok: false, error: 'missing' }
    try {
      const db = getDb()
      const tokenSnap = await get(ref(db, `${ROOT}/tokens/${tokenId}`))
      const tokenData = tokenSnap.val()
      if (!tokenData) return { ok: false, error: 'not_found' }
      if (tokenData.role !== 'manager') return { ok: false, error: 'wrong_role' }
      if (tokenData.isUsed || tokenData.usedAt) return { ok: false, error: 'already_used' }
      const recordSnap = await get(ref(db, `${ROOT}/records/${tokenData.recordId}`))
      const record = recordSnap.val()
      if (!record) return { ok: false, error: 'record_missing' }
      if (record.status !== RECORD_STATUS.MANAGER_PENDING) {
      return {
          ok: false,
          error: 'already_decided',
          status: record.status,
          recordNumber: record.recordNumber || null,
        }
      }
      return { ok: true, token: tokenData, record: { id: tokenData.recordId, ...record } }
    } catch (error) {
      console.log('getManagerEvaluationByToken:', error)
      return { ok: false, error: 'load_failed' }
    }
  }, [])

  /** Yönetici kabul/red + uygulama tanımı */
  const submitManagerDecision = useCallback(async (tokenId, decisionPayload) => {
      const db = getDb()
    const now = new Date().toISOString()
    try {
      const tokenData = await claimUnusedToken(db, tokenId, { expectedRole: 'manager', usedAt: now })
      const recordId = tokenData.recordId
      const recordSnap = await get(ref(db, `${ROOT}/records/${recordId}`))
      const record = recordSnap.val()
      if (!record || record.status !== RECORD_STATUS.MANAGER_PENDING) {
        throw new Error('Kayıt yönetici kararında değil.')
      }

      if (decisionPayload.result === MANAGER_DECISION.REJECT) {
        await update(ref(db, `${ROOT}/records/${recordId}`), {
          status: RECORD_STATUS.CLOSED_REJECTED_MANAGER,
          closedAt: now,
          dueAt: null,
          assignee: null,
          managerDecision: {
            result: MANAGER_DECISION.REJECT,
            reasons: decisionPayload.reasons || [],
            justification: decisionPayload.justification || '',
            evaluatorName: decisionPayload.evaluatorName || tokenData.memberName || '',
            decidedAt: now,
          },
        })
        await pushHistory(db, recordId, 'manager_rejected', decisionPayload.evaluatorName || 'manager', {
          message: 'Yönetici reddetti',
          reasons: decisionPayload.reasons || [],
        })
        if (record.applicant?.email) {
          await sendOdulOneriMail(
            {
              to: record.applicant.email,
              subject: `Öneri Sonucu — ${record.recordNumber}`,
              message: `<div>
                <p>Sayın ${escapeHtml(record.applicant.fullName || '')},</p>
                <p><b>${escapeHtml(record.recordNumber)}</b> numaralı öneriniz ilgili bölüm yöneticisi tarafından kabul edilmedi.</p>
                <p><b>Gerekçe:</b> ${escapeHtml(decisionPayload.justification || '')}</p>
              </div>`,
              meta: { recordId, recordNumber: record.recordNumber },
            },
            db
          )
        }
        toastSuccessNotify('Red kararı kaydedildi.')
        return { status: RECORD_STATUS.CLOSED_REJECTED_MANAGER }
      }

      // Accept → open implementation
      const year = new Date().getFullYear()
      const uySeq = await nextSequence(db, `${ROOT}/meta/uygulamaSequence/${year}`)
      const uygulamaNo = formatUygulamaNumber(year, uySeq)
          const implId = uid()
      const implementation = buildImplementationFromAccept({
            implId,
            uygulamaNo,
            recordId,
        record,
        acceptPayload: decisionPayload,
        createdAt: now,
      })

      const leaderEmail = implementation.plan?.leaderEmail || ''
      let trackingAccessTokenId = null
      if (hasValidEmail(leaderEmail)) {
        const accessToken = createTrackingAccessTokenPayload(leaderEmail)
        trackingAccessTokenId = accessToken.tokenId
        implementation.leaderTokenId = trackingAccessTokenId
        await set(ref(db, `${ROOT}/tokens/${accessToken.tokenId}`), {
          ...accessToken,
          recordId,
          implementationId: implId,
        })
      }

      await update(ref(db), {
        [`${ROOT}/implementations/${implId}`]: implementation,
        [`${ROOT}/records/${recordId}/status`]: RECORD_STATUS.IMPL_NOT_STARTED,
        [`${ROOT}/records/${recordId}/implementationId`]: implId,
        [`${ROOT}/records/${recordId}/uygulamaNo`]: uygulamaNo,
        [`${ROOT}/records/${recordId}/dueAt`]: null,
        [`${ROOT}/records/${recordId}/assignee`]: {
          type: 'leader',
          id: implId,
          name: implementation.plan?.leaderName || '',
          email: leaderEmail,
        },
        [`${ROOT}/records/${recordId}/managerDecision`]: {
          result: MANAGER_DECISION.ACCEPT,
          method: decisionPayload.method,
          plan: decisionPayload.plan,
          benefit: decisionPayload.benefit,
          evaluatorName: decisionPayload.evaluatorName || '',
          decidedAt: now,
        },
      })
      await pushHistory(db, recordId, 'manager_accepted', decisionPayload.evaluatorName || 'manager', {
        message: `Kabul edildi; uygulama açıldı (${uygulamaNo})`,
        implementationId: implId,
      })

      if (trackingAccessTokenId && hasValidEmail(leaderEmail)) {
        const url = `${window.location.origin}/odul-oneri/opex-takip?access=${encodeURIComponent(trackingAccessTokenId)}&implementationId=${encodeURIComponent(implId)}`
        await sendOdulOneriMail(
          {
            to: leaderEmail,
            subject: `Uygulama Takibi — ${uygulamaNo}`,
            message: `<div>
              <p>Sayın ${escapeHtml(implementation.plan?.leaderName || '')},</p>
              <p><b>${escapeHtml(record.recordNumber)}</b> / <b>${escapeHtml(uygulamaNo)}</b> için uygulama lideri olarak atandınız.</p>
              <p>Bağlantı e-posta adresinizi doğrular; yalnızca size atanmış uygulamaları görürsünüz.</p>
              <p><a href="${escapeHtml(url)}">Uygulama takip ekranını aç</a></p>
              <p style="color:#667085;font-size:12px">Bağlantı 48 saat geçerlidir. Süresi biterse takip ekranından e-posta ile yeniden giriş isteyebilirsiniz.</p>
              </div>`,
            meta: { recordId, recordNumber: record.recordNumber },
          },
          db
        )
      }
        if (record.applicant?.email) {
        await sendOdulOneriMail(
          {
              to: record.applicant.email,
            subject: `Öneri Kabul Edildi — ${record.recordNumber}`,
              message: `<div>
              <p>Sayın ${escapeHtml(record.applicant.fullName || '')},</p>
              <p>Öneriniz kabul edildi ve uygulama planı oluşturuldu (<b>${escapeHtml(uygulamaNo)}</b>).</p>
              </div>`,
              meta: { recordId, recordNumber: record.recordNumber },
          },
          db
        )
      }
      toastSuccessNotify(`Kabul edildi. Uygulama No: ${uygulamaNo}`)
      return { status: RECORD_STATUS.IMPL_NOT_STARTED, implementationId: implId, uygulamaNo }
    } catch (error) {
      console.log('submitManagerDecision:', error)
      toastErrorNotify(error?.message || 'Yönetici kararı kaydedilemedi.')
      throw error
    }
  }, [])

  const fetchManagerPendingTasks = useCallback(async (managerEmail) => {
    const email = String(managerEmail || '').trim().toLowerCase()
    if (!email) return []
    const snap = await get(ref(getDb(), `${ROOT}/records`))
    const all = snap.val() || {}
    return Object.entries(all)
      .filter(
        ([, r]) =>
          r?.status === RECORD_STATUS.MANAGER_PENDING &&
          String(r?.assignee?.email || r?.relatedDepartmentManagerEmail || '')
          .trim()
          .toLowerCase() === email
    )
      .map(([id, r]) => ({ id, ...r }))
  }, [])

  /** Eski lider token → e-posta oturumu (uygulama sayfası kaldırıldı) */
  const getImplementationByToken = useCallback(async (tokenId) => {
    if (!tokenId) return { ok: false, error: 'missing' }
    try {
      const db = getDb()
      const tokenSnap = await get(ref(db, `${ROOT}/tokens/${tokenId}`))
      const tokenData = tokenSnap.val()
      if (!tokenData) return { ok: false, error: 'not_found' }
      if (
        tokenData.role !== 'implementation_leader' &&
        tokenData.role !== TRACKING_ACCESS_ROLE &&
        tokenData.role !== OPEX_ACCESS_ROLE
      ) {
        return { ok: false, error: 'wrong_role' }
      }
      if (
        tokenData.role === TRACKING_ACCESS_ROLE ||
        tokenData.role === OPEX_ACCESS_ROLE
      ) {
        return { ok: false, error: 'use_claim_tracking', token: tokenData }
      }
      const implId = tokenData.implementationId
      const implSnap = await get(ref(db, `${ROOT}/implementations/${implId}`))
      const impl = implSnap.val()
      if (!impl) return { ok: false, error: 'impl_missing' }
      const recordSnap = await get(ref(db, `${ROOT}/records/${tokenData.recordId}`))
      const record = recordSnap.val()
      const recordObj = record ? { id: tokenData.recordId, ...record } : null
      const implObj = { id: implId, ...impl }
      if (tokenData.isUsed || tokenData.usedAt || isLeaderAccessClosed(recordObj, implObj)) {
        return { ok: false, error: 'process_closed', implementation: implObj, record: recordObj }
      }
      return {
        ok: true,
        token: tokenData,
        implementation: implObj,
        record: recordObj,
      }
    } catch (error) {
      console.log('getImplementationByToken:', error)
      return { ok: false, error: 'load_failed' }
    }
  }, [])

  /** OPEX ön filtre — yalnızca config/opexUsers e-postaları */
  const requestOpexAccess = useCallback(async (email) => {
    const normalized = String(email || '').trim().toLowerCase()
    const neutral =
      'E-posta OPEX listesindeyse giriş bağlantısı gönderildi. Gelen kutunuzu kontrol edin.'
    if (!hasValidEmail(normalized)) {
      toastErrorNotify('Geçerli bir e-posta girin.')
      return { ok: false, error: 'invalid_email' }
    }
    try {
      const db = getDb()
      const opexUsers = await loadActiveOpexUsers(db)
      const match = opexUsers.find((u) => u.email === normalized)
      if (!match) {
        toastSuccessNotify(neutral)
        return { ok: true, sent: false }
      }
      const accessToken = createOpexAccessTokenPayload(normalized)
      await set(ref(db, `${ROOT}/tokens/${accessToken.tokenId}`), accessToken)
      const url = `${window.location.origin}/odul-oneri/opex-filtre?access=${encodeURIComponent(accessToken.tokenId)}`
      await sendOdulOneriMail(
        {
          to: normalized,
          subject: 'OPEX Ön İnceleme — Giriş bağlantısı',
          message: `<div>
            <p>Sayın ${escapeHtml(match.fullName || '')},</p>
            <p>OPEX ön inceleme ekranına giriş için bağlantınız:</p>
            <p><a href="${escapeHtml(url)}">Ön incelemeyi aç</a></p>
            <p style="color:#667085;font-size:12px">Bağlantı 48 saat geçerlidir ve tek kullanımlıktır.</p>
          </div>`,
          meta: {},
        },
        db
      )
      toastSuccessNotify(neutral)
      return { ok: true, sent: true }
    } catch (error) {
      console.log('requestOpexAccess:', error)
      toastErrorNotify(error?.message || 'Giriş bağlantısı gönderilemedi.')
      return { ok: false, error: 'failed' }
    }
  }, [])

  /**
   * E-posta magic link iste (lider / yönetici takip).
   * Enumerasyonu zayıflatmak için her zaman nötr başarı mesajı.
   */
  const requestTrackingAccess = useCallback(async (email) => {
    const normalized = String(email || '').trim().toLowerCase()
    const neutral =
      'E-posta sistemde kayıtlıysa giriş bağlantısı gönderildi. Gelen kutunuzu kontrol edin.'
    if (!hasValidEmail(normalized)) {
      toastErrorNotify('Geçerli bir e-posta girin.')
      return { ok: false, error: 'invalid_email' }
    }
    try {
      const db = getDb()
      const [implSnap, recordsSnap] = await Promise.all([
        get(ref(db, `${ROOT}/implementations`)),
        get(ref(db, `${ROOT}/records`)),
      ])
      const impls = implSnap.val() || {}
      const records = recordsSnap.val() || {}
      let eligible = false
      for (const [implId, impl] of Object.entries(impls)) {
        const recordId = impl?.recordId
        const record = recordId ? { id: recordId, ...(records[recordId] || {}) } : null
        if (!emailMatchesImplementation(normalized, impl, record)) continue
        if (isLeaderAccessClosed(record, { id: implId, ...impl })) continue
        eligible = true
        break
      }
      if (!eligible) {
        toastSuccessNotify(neutral)
        return { ok: true, sent: false }
      }
      const accessToken = createTrackingAccessTokenPayload(normalized)
      await set(ref(db, `${ROOT}/tokens/${accessToken.tokenId}`), accessToken)
      const url = `${window.location.origin}/odul-oneri/opex-takip?access=${encodeURIComponent(accessToken.tokenId)}`
      await sendOdulOneriMail(
        {
          to: normalized,
          subject: 'Uygulama Takibi — Giriş bağlantısı',
          message: `<div>
            <p>Ödül Öneri uygulama takip ekranına giriş için bağlantınız:</p>
            <p><a href="${escapeHtml(url)}">Takip ekranını aç</a></p>
            <p style="color:#667085;font-size:12px">Bağlantı ${48} saat geçerlidir ve tek kullanımlıktır. Yalnızca size atanmış uygulamaları görürsünüz.</p>
          </div>`,
          meta: {},
        },
        db
      )
      toastSuccessNotify(neutral)
      return { ok: true, sent: true }
    } catch (error) {
      console.log('requestTrackingAccess:', error)
      toastErrorNotify(error?.message || 'Giriş bağlantısı gönderilemedi.')
      return { ok: false, error: 'failed' }
    }
  }, [])

  /** Magic link / eski lider token → e-posta oturumu */
  const claimTrackingAccess = useCallback(async (tokenId) => {
    if (!tokenId) return { ok: false, error: 'missing' }
    try {
      const db = getDb()
      const tokenSnap = await get(ref(db, `${ROOT}/tokens/${tokenId}`))
      const tokenData = tokenSnap.val()
      if (!tokenData) return { ok: false, error: 'not_found' }

      if (
        tokenData.role === TRACKING_ACCESS_ROLE ||
        tokenData.role === OPEX_ACCESS_ROLE
      ) {
        if (
          tokenData.expiresAt &&
          new Date(tokenData.expiresAt).getTime() < Date.now()
        ) {
          return { ok: false, error: 'expired' }
        }
        const email = String(tokenData.email || '').trim().toLowerCase()
        if (!hasValidEmail(email)) return { ok: false, error: 'invalid_email' }
        // OPEX link: süre dolana kadar tekrar tıklanabilir. Takip linki tek kullanımlık.
        if (tokenData.role === TRACKING_ACCESS_ROLE) {
          if (tokenData.isUsed || tokenData.usedAt) {
            return { ok: false, error: 'used' }
          }
          await update(ref(db, `${ROOT}/tokens/${tokenId}`), {
            isUsed: true,
            usedAt: new Date().toISOString(),
          })
          const session = writeTrackingSession(email)
          return {
            ok: true,
            email,
            session,
            kind: 'tracking',
            implementationId: tokenData.implementationId || null,
          }
        }
        if (!tokenData.usedAt) {
          await update(ref(db, `${ROOT}/tokens/${tokenId}`), {
            usedAt: new Date().toISOString(),
          })
        }
        const session = writeOpexSession(email)
        return {
          ok: true,
          email,
          session,
          kind: 'opex',
          recordId: tokenData.recordId || null,
          implementationId: tokenData.implementationId || null,
        }
      }

      if (tokenData.role === 'implementation_leader') {
        const loaded = await getImplementationByToken(tokenId)
        if (!loaded.ok && loaded.error !== 'process_closed') {
          return { ok: false, error: loaded.error || 'not_found' }
        }
        const email = String(
          loaded.implementation?.plan?.leaderEmail || ''
        )
          .trim()
          .toLowerCase()
        if (!hasValidEmail(email)) return { ok: false, error: 'invalid_email' }
        const session = writeTrackingSession(email)
        return {
          ok: true,
          email,
          session,
          implementationId: loaded.implementation?.id || tokenData.implementationId || null,
          legacy: true,
        }
      }

      return { ok: false, error: 'wrong_role' }
    } catch (error) {
      console.log('claimTrackingAccess:', error)
      return { ok: false, error: 'load_failed' }
    }
  }, [getImplementationByToken])

  const fetchImplementations = useCallback(async () => {
    const snap = await get(ref(getDb(), `${ROOT}/implementations`))
    const all = snap.val() || {}
      return Object.entries(all)
      .map(([id, row]) => ({ id, ...row }))
        .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
  }, [])

  const updateImplementationTracking = useCallback(
    async (
      implementationId,
      patch,
      { recordStatus = null, actor = '', requireAssigneeMatch = false } = {}
    ) => {
      const db = getDb()
      const now = new Date().toISOString()
      try {
        const implSnap = await get(ref(db, `${ROOT}/implementations/${implementationId}`))
        const impl = implSnap.val()
        if (!impl) throw new Error('Uygulama kaydı bulunamadı.')

        if (requireAssigneeMatch) {
          const actorEmail = String(actor || '').trim().toLowerCase()
          let record = null
          if (impl.recordId) {
            const recordSnap = await get(ref(db, `${ROOT}/records/${impl.recordId}`))
            const val = recordSnap.val()
            record = val ? { id: impl.recordId, ...val } : null
          }
          if (!emailMatchesImplementation(actorEmail, impl, record)) {
            throw new Error('Bu kaydı güncelleme yetkiniz yok.')
          }
        }

        const updates = {
          [`${ROOT}/implementations/${implementationId}/updatedAt`]: now,
          [`${ROOT}/implementations/${implementationId}/tracking`]: {
            ...(impl.tracking || {}),
            ...(patch.tracking || {}),
            lastUpdateAt: now,
            lastUpdateBy: actor || patch.tracking?.lastUpdateBy || '',
          },
        }
        if (patch.realization) {
          updates[`${ROOT}/implementations/${implementationId}/realization`] = patch.realization
        }
        if (patch.opexVerification !== undefined) {
          updates[`${ROOT}/implementations/${implementationId}/opexVerification`] =
            patch.opexVerification
        }
        let opexUsersForNotify = null
        if (recordStatus && impl.recordId) {
          updates[`${ROOT}/records/${impl.recordId}/status`] = recordStatus
          if (recordStatus === RECORD_STATUS.AWARD_POOL) {
            updates[`${ROOT}/records/${impl.recordId}/assignee`] = {
              type: 'board',
              id: 'board',
              name: 'Öneri Kurulu',
              email: '',
            }
          }
          if (recordStatus === RECORD_STATUS.IMPL_CORRECTION) {
            updates[`${ROOT}/records/${impl.recordId}/assignee`] = {
              type: 'leader',
              id: implementationId,
              name: impl.plan?.leaderName || '',
              email: impl.plan?.leaderEmail || '',
            }
          }
          if (recordStatus === RECORD_STATUS.IMPL_RESULT_PENDING) {
            opexUsersForNotify = await loadActiveOpexUsers(db)
            updates[`${ROOT}/records/${impl.recordId}/assignee`] =
              buildOpexAssignee(opexUsersForNotify)
          }
        }
        await update(ref(db), updates)

        const shouldCloseLeaderToken =
          recordStatus === RECORD_STATUS.AWARD_POOL ||
          recordStatus === RECORD_STATUS.IMPL_CANCELLED ||
          patch.opexVerification?.result === 'verified' ||
          (recordStatus && String(recordStatus).startsWith('closed_'))
        if (shouldCloseLeaderToken) {
          await invalidateLeaderTokensForImplementation(
            db,
            implementationId,
            impl.leaderTokenId || null
          )
        }

        if (impl.recordId) {
          await pushHistory(
            db,
            impl.recordId,
            patch.historyType || 'implementation_updated',
            actor || 'system',
            {
              message: patch.historyMessage || 'Uygulama güncellendi',
              recordStatus,
              statusNote: patch.tracking?.statusNote || null,
            }
          )
        }
        if (
          recordStatus === RECORD_STATUS.IMPL_RESULT_PENDING &&
          impl.recordId &&
          opexUsersForNotify
        ) {
          const recordSnap = await get(ref(db, `${ROOT}/records/${impl.recordId}`))
          const record = recordSnap.val() || {}
          const takipUrl = `${window.location.origin}/odul-oneri/opex-takip?implementationId=${encodeURIComponent(implementationId)}`
          await notifyActiveOpexUsers(db, opexUsersForNotify, {
            subject: `Sonuç Doğrulama Bekliyor — ${record.recordNumber || impl.uygulamaNo || ''}`,
            messageHtml: `<div>
              <p>Uygulama sonuç doğrulama için OPEX’e düştü.</p>
              <p><b>Kayıt No:</b> ${escapeHtml(record.recordNumber || '')}</p>
              <p><b>Uygulama No:</b> ${escapeHtml(impl.uygulamaNo || '')}</p>
              <p><b>Konu:</b> ${escapeHtml(record.topic || '')}</p>
              <p>Kayıt sonuç doğrulama için OPEX havuzundadır.</p>
              <p><a href="${takipUrl}">Uygulama takibinde aç</a></p>
            </div>`,
            meta: {
              recordId: impl.recordId,
              recordNumber: record.recordNumber || null,
            },
          })
        }
        toastSuccessNotify('Uygulama kaydı güncellendi.')
        return true
      } catch (error) {
        console.log('updateImplementationTracking:', error)
        toastErrorNotify(error?.message || 'Güncelleme başarısız.')
        throw error
      }
    },
    []
  )

  const verifyImplementation = useCallback(
    async (
      implementationId,
      verification,
      { approve, actor = '', realization = null, historyMessage = '' } = {}
    ) => {
      if (approve) {
        return updateImplementationTracking(
          implementationId,
          {
            ...(realization ? { realization } : {}),
            opexVerification: {
              ...verification,
              result: 'verified',
              verifiedAt: new Date().toISOString(),
              verifiedBy: actor,
            },
            historyMessage:
              historyMessage || 'OPEX doğruladı; ödül havuzuna alındı',
            historyType: 'opex_verified',
          },
          { recordStatus: RECORD_STATUS.AWARD_POOL, actor }
        )
      }
      return updateImplementationTracking(
        implementationId,
        {
          opexVerification: {
            ...verification,
            result: 'returned',
            returnedAt: new Date().toISOString(),
            returnedBy: actor,
          },
          historyMessage: verification?.note
            ? `OPEX düzeltme istedi: ${verification.note}`
            : 'OPEX düzeltme istedi',
          historyType: 'opex_correction',
        },
        { recordStatus: RECORD_STATUS.IMPL_CORRECTION, actor }
      )
    },
    [updateImplementationTracking]
  )

  const fetchRecordHistory = useCallback(async (recordId) => {
    if (!recordId) return []
    const snap = await get(ref(getDb(), `${ROOT}/history/${recordId}`))
    const all = snap.val() || {}
    return Object.entries(all)
      .map(([id, row]) => ({ id, ...row }))
      .sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0))
  }, [])

  const fetchAwardPool = useCallback(async () => {
    const [records, implementations] = await Promise.all([
      fetchAllOdulOneriRecords(),
      fetchImplementations(),
    ])
    const implById = Object.fromEntries(implementations.map((i) => [i.id, i]))
    return records
      .filter((r) => r.status === RECORD_STATUS.AWARD_POOL || r.status === RECORD_STATUS.DEFERRED)
      .map((r) => ({
        ...r,
        implementation: r.implementationId ? implById[r.implementationId] : null,
      }))
  }, [fetchAllOdulOneriRecords, fetchImplementations])

  /** Kurul toplantısı — karşılaştırmalı puanlama + karar */
  const saveBoardMeetingDecisions = useCallback(
    async ({ attendees, items, meetingNote = '', actor = '' }) => {
      const db = getDb()
      const now = new Date().toISOString()
      const meetingId = uid()
      try {
        const n = Number(attendees)
        if (!Number.isFinite(n) || n < 1) throw new Error('Katılan üye sayısı geçersiz.')

        const decisions = []
        for (const item of items || []) {
          const active = getActiveCriteriaForTopic(item.topic)
          for (const criterion of active) {
            const i = BOARD_CRITERIA.findIndex((c) => c.id === criterion.id)
            if (i < 0) continue
            const voteErr = validateVoteRow(item.votes?.[i], n)
            if (voteErr) {
              throw new Error(
                `${item.recordNumber || item.recordId}: «${criterion.title}» — ${voteErr}`
              )
            }
          }
          const activeIds = new Set(active.map((c) => c.id))
          // Kapsam dışı (ağırlık 0) kriterlerde oy yok → ortalama 0; aksi halde null skoru bozar
          const averages = BOARD_CRITERIA.map((criterion, i) => {
            if (!activeIds.has(criterion.id)) return 0
            return criterionAverageFromVotes(item.votes?.[i], n)
          })
          const score = getMeetingWeightedScore(averages, item.topic)
          if (score == null) {
            throw new Error(
              `${item.recordNumber || item.recordId}: kurul puanı hesaplanamadı (oylar / konu).`
            )
          }
          const weights = getTopicWeights(item.topic)
          if (!item.result || !Object.values(BOARD_DECISION).includes(item.result)) {
            throw new Error(`${item.recordNumber}: kurul kararı seçilmelidir.`)
          }
          if (!String(item.justification || '').trim() || String(item.justification).trim().length < 8) {
            throw new Error(`${item.recordNumber}: karar gerekçesi en az 8 karakter olmalıdır.`)
          }
          decisions.push({
            recordId: item.recordId,
            implementationId: item.implementationId,
            recordNumber: item.recordNumber,
            topic: item.topic,
            votes: item.votes,
            averages,
            groupScore: score,
            weights,
            result: item.result,
            justification: String(item.justification).trim(),
          })
        }

        const meeting = {
          id: meetingId,
          attendees: n,
          note: meetingNote,
          decidedAt: now,
          decidedBy: actor,
          items: decisions,
        }
        await set(ref(db, `${ROOT}/boardMeetings/${meetingId}`), meeting)

        const multi = {}
        for (const d of decisions) {
          let nextStatus = RECORD_STATUS.CLOSED_THANKS
          if (d.result === BOARD_DECISION.AWARD) nextStatus = RECORD_STATUS.CLOSED_AWARDED
          if (d.result === BOARD_DECISION.DEFERRED) nextStatus = RECORD_STATUS.DEFERRED
          const boardDecision = {
            result: d.result,
            justification: d.justification,
            groupScore: d.groupScore,
            topic: d.topic,
            averages: d.averages,
            votes: d.votes,
            weights: d.weights,
            attendees: n,
            meetingId,
            decidedAt: now,
            decidedBy: actor,
          }
          multi[`${ROOT}/records/${d.recordId}/boardDecision`] = boardDecision
          multi[`${ROOT}/records/${d.recordId}/status`] = nextStatus
          if (nextStatus !== RECORD_STATUS.DEFERRED) {
            multi[`${ROOT}/records/${d.recordId}/closedAt`] = now
            multi[`${ROOT}/records/${d.recordId}/assignee`] = null
          }
          if (d.implementationId) {
            multi[`${ROOT}/implementations/${d.implementationId}/boardDecision`] = boardDecision
            multi[`${ROOT}/implementations/${d.implementationId}/updatedAt`] = now
          }
        }
        await update(ref(db), multi)

        for (const d of decisions) {
          await pushHistory(db, d.recordId, 'board_decision', actor || 'opex', {
            message: `Kurul kararı: ${d.result} (puan ${formatWeightedScore(d.groupScore)})`,
            meetingId,
            groupScore: d.groupScore,
          })
        }
        toastSuccessNotify(`Kurul toplantısı kaydedildi (${decisions.length} öneri).`)
        return { meetingId, count: decisions.length }
      } catch (error) {
        console.log('saveBoardMeetingDecisions:', error)
        toastErrorNotify(error?.message || 'Kurul kararları kaydedilemedi.')
        throw error
      }
    },
    []
  )

  const fetchMailFailures = useCallback(async () => {
    const snap = await get(ref(getDb(), `${ROOT}/mailFailures`))
    const all = snap.val() || {}
      return Object.entries(all)
        .map(([id, row]) => ({ id, ...row }))
      .filter((row) => !row.resolved)
      .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
  }, [])

  const resolveMailFailure = useCallback(async (id) => {
    await update(ref(getDb(), `${ROOT}/mailFailures/${id}`), {
      resolved: true,
      resolvedAt: new Date().toISOString(),
    })
    toastSuccessNotify('Mail kaydı çözüldü olarak işaretlendi.')
  }, [])

  const isDepartmentManagerEmail = useCallback(
    async (email) => {
      const e = String(email || '').trim().toLowerCase()
      if (!e) return false
      const departments = await fetchDepartments()
      return departments.some((d) => String(d.managerEmail || '').trim().toLowerCase() === e)
    },
    [fetchDepartments]
  )

  const isBoardMemberEmail = useCallback(
    async (email) => {
      const e = String(email || '').trim().toLowerCase()
      if (!e) return false
      const members = await fetchBoardMembers()
      return members.some((m) => String(m.email || '').trim().toLowerCase() === e)
    },
    [fetchBoardMembers]
  )

  const processSlaReminders = useCallback(async () => {
      const db = getDb()
    const snap = await get(ref(db, `${ROOT}/records`))
    const all = snap.val() || {}
    let sent = 0
    for (const [recordId, record] of Object.entries(all)) {
      if (record?.status !== RECORD_STATUS.MANAGER_PENDING) continue
      const refDate = record.createdAt || record.dueAt
      if (!isPastReminderThreshold(refDate, new Date(), REMINDER_AFTER_DAYS)) continue
      if (!canSendReminder(record.lastReminderAt || record.reminders?.manager?.sentAt)) continue
      const to = record.assignee?.email || record.relatedDepartmentManagerEmail
      if (!to) continue
      const ok = await sendOdulOneriMail(
        {
          to,
          subject: `Hatırlatma — ${record.recordNumber}`,
          message: `<div>
            <p>Açık öneri göreviniz için hatırlatma.</p>
            <p><b>Kayıt:</b> ${escapeHtml(record.recordNumber)}</p>
            <p><b>Durum:</b> ${escapeHtml(record.status)}</p>
          </div>`,
          meta: { recordId, recordNumber: record.recordNumber },
        },
        db
      )
      if (ok) {
        sent += 1
        await update(ref(db, `${ROOT}/records/${recordId}`), {
          lastReminderAt: new Date().toISOString(),
          'reminders/manager/sentAt': new Date().toISOString(),
        })
      }
    }
    return { sent }
  }, [])

  /** Süreç test verisini siler; config/departments, boardMembers ve opexUsers korunur */
  const wipeProcessData = useCallback(async () => {
    const db = getDb()
    await Promise.all([
      remove(ref(db, `${ROOT}/records`)),
      remove(ref(db, `${ROOT}/tokens`)),
      remove(ref(db, `${ROOT}/history`)),
      remove(ref(db, `${ROOT}/implementations`)),
      remove(ref(db, `${ROOT}/boardMeetings`)),
      remove(ref(db, `${ROOT}/mailFailures`)),
      remove(ref(db, `${ROOT}/meta`)),
    ])
    // set: eski quorum/threshold vb. anahtarları da siler
    await set(ref(db, `${ROOT}/config/settings`), {
      sequence: 0,
      slaDays: DEFAULT_SLA_DAYS,
      flowVersion: 2,
    })
    toastSuccessNotify('Süreç verileri temizlendi (bölüm/kurul/OPEX config korundu).')
    return true
  }, [])

  return {
    createOdulOneri,
    uploadCurrentStateAttachments,
    uploadResultAttachments,
    fetchDepartments,
    fetchBoardMembers,
    fetchOpexPendingRecords,
    fetchOdulOneriRecordById,
    fetchAllOdulOneriRecords,
    submitOpexDecision,
    getManagerEvaluationByToken,
    submitManagerDecision,
    fetchManagerPendingTasks,
    getImplementationByToken,
    requestTrackingAccess,
    requestOpexAccess,
    claimTrackingAccess,
    fetchImplementations,
    updateImplementationTracking,
    verifyImplementation,
    fetchRecordHistory,
    fetchAwardPool,
    saveBoardMeetingDecisions,
    fetchMailFailures,
    resolveMailFailure,
    processSlaReminders,
    isDepartmentManagerEmail,
    isBoardMemberEmail,
    wipeProcessData,
  }
}

export default useOdulOneriCall
